import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../ManageSettings/Settings.css";
import { ToastContainer } from "react-toastify";
import {
  getFilteredApps,
  getMenuItemsForApp,
  getEnhancedInitialRoute,
} from "../../../common/appRoutes";
import { useToastHandler } from "../../../common/ToastHandler";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import useMyPermissions from "../../../../utils/permissions/useMyPermissions";
import Swal from "sweetalert2";
import axios from "axios";
import config from "../../../../config/Global.json";
import { applyCatalogLayout, startsCatalogSection } from "../../../common/appCatalogLayout";

const visualPresets = {
  lottery: {
    type: "lottery",
    accent: "#7c3aed",
    soft: "#f3e8ff",
    border: "#ddd6fe",
    label: "Lottery",
  },
  inventory: {
    type: "inventory",
    accent: "#2563eb",
    soft: "#dbeafe",
    border: "#bfdbfe",
    label: "Inventory",
  },
  pos: {
    type: "pos",
    accent: "#0891b2",
    soft: "#cffafe",
    border: "#a5f3fc",
    label: "POS",
  },
  sales: {
    type: "sales",
    accent: "#059669",
    soft: "#d1fae5",
    border: "#a7f3d0",
    label: "Sales",
  },
  payroll: {
    type: "payroll",
    accent: "#ea580c",
    soft: "#ffedd5",
    border: "#fed7aa",
    label: "Payroll",
  },
  expense: {
    type: "expense",
    accent: "#dc2626",
    soft: "#fee2e2",
    border: "#fecaca",
    label: "Expense",
  },
  report: {
    type: "report",
    accent: "#4f46e5",
    soft: "#e0e7ff",
    border: "#c7d2fe",
    label: "Report",
  },
  gas: {
    type: "gas",
    accent: "#ca8a04",
    soft: "#fef3c7",
    border: "#fde68a",
    label: "Gas",
  },
  legal: {
    type: "legal",
    accent: "#475569",
    soft: "#f1f5f9",
    border: "#cbd5e1",
    label: "Legal",
  },
  settings: {
    type: "settings",
    accent: "#0f766e",
    soft: "#ccfbf1",
    border: "#99f6e4",
    label: "Settings",
  },
  default: {
    type: "default",
    accent: "#334155",
    soft: "#f1f5f9",
    border: "#cbd5e1",
    label: "App",
  },
};

const getVisualPreset = (app) => {
  const keyword = `${app?.key || ""} ${app?.name || ""} ${app?.displayName || ""}`.toLowerCase();

  if (keyword.includes("lottery")) return visualPresets.lottery;
  if (keyword.includes("inventory") || keyword.includes("product") || keyword.includes("stock")) return visualPresets.inventory;
  if (keyword.includes("pos") || keyword.includes("quick")) return visualPresets.pos;
  if (keyword.includes("invoice") || keyword.includes("sale") || keyword.includes("billing")) return visualPresets.sales;
  if (keyword.includes("payroll") || keyword.includes("employee") || keyword.includes("staff")) return visualPresets.payroll;
  if (keyword.includes("purchase") || keyword.includes("expense")) return visualPresets.expense;
  if (keyword.includes("report") || keyword.includes("analytics") || keyword.includes("dashboard")) return visualPresets.report;
  if (keyword.includes("gas") || keyword.includes("fuel")) return visualPresets.gas;
  if (keyword.includes("legal") || keyword.includes("case") || keyword.includes("law")) return visualPresets.legal;
  if (keyword.includes("setting") || keyword.includes("admin") || keyword.includes("manage")) return visualPresets.settings;

  return visualPresets.default;
};

const IconWrapper = ({ children, preset, large = false }) => (
  <div
    className={large ? "pv4-icon-tile pv4-icon-tile-large" : "pv4-icon-tile"}
    style={{
      "--pv4-accent": preset.accent,
      "--pv4-soft": preset.soft,
      "--pv4-border": preset.border,
    }}
  >
    {children}
  </div>
);

const CategoryIcon = ({ preset, large = false }) => {
  const size = large ? 46 : 34;
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 48 48",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
  };

  const stroke = preset.accent;

  switch (preset.type) {
    case "lottery":
      return (
        <IconWrapper preset={preset} large={large}>
          <svg {...common}>
            <path d="M12 18.5 29.8 8.2 36 19 18.2 29.3 12 18.5Z" fill={preset.soft} stroke={stroke} strokeWidth="2.4" />
            <path d="M18.5 17.5 28 12M21.5 22.7 31 17.2" stroke={stroke} strokeWidth="2.2" strokeLinecap="round" />
            <path d="M14 28h24v11H14V28Z" fill="#fff" stroke={stroke} strokeWidth="2.4" />
            <path d="M20 33h12" stroke={stroke} strokeWidth="2.3" strokeLinecap="round" />
          </svg>
        </IconWrapper>
      );
    case "inventory":
      return (
        <IconWrapper preset={preset} large={large}>
          <svg {...common}>
            <path d="M8 17 24 8l16 9-16 9-16-9Z" fill={preset.soft} stroke={stroke} strokeWidth="2.4" />
            <path d="M8 17v16l16 9V26L8 17ZM40 17v16l-16 9V26l16-9Z" fill="#fff" stroke={stroke} strokeWidth="2.4" />
            <path d="M18 13.5 34 22.5" stroke={stroke} strokeWidth="2.2" strokeLinecap="round" />
          </svg>
        </IconWrapper>
      );
    case "pos":
      return (
        <IconWrapper preset={preset} large={large}>
          <svg {...common}>
            <path d="M13 13h20l3 16H16L13 13Z" fill={preset.soft} stroke={stroke} strokeWidth="2.4" />
            <path d="M11 13H8M18 35a2.5 2.5 0 1 0 0 .1M33 35a2.5 2.5 0 1 0 0 .1" stroke={stroke} strokeWidth="2.6" strokeLinecap="round" />
            <path d="M20 20h10M20 25h7" stroke={stroke} strokeWidth="2.3" strokeLinecap="round" />
          </svg>
        </IconWrapper>
      );
    case "sales":
      return (
        <IconWrapper preset={preset} large={large}>
          <svg {...common}>
            <path d="M14 8h20v32l-4-3-4 3-4-3-4 3-4-3V8Z" fill="#fff" stroke={stroke} strokeWidth="2.4" />
            <path d="M19 17h10M19 23h10M19 29h6" stroke={stroke} strokeWidth="2.3" strokeLinecap="round" />
            <path d="M32 30c3.3 0 6 2.7 6 6s-2.7 6-6 6-6-2.7-6-6 2.7-6 6-6Z" fill={preset.soft} stroke={stroke} strokeWidth="2.4" />
            <path d="M32 33v6M29.8 34.3h3.4a1.8 1.8 0 0 1 0 3.6h-2.4" stroke={stroke} strokeWidth="1.9" strokeLinecap="round" />
          </svg>
        </IconWrapper>
      );
    case "payroll":
      return (
        <IconWrapper preset={preset} large={large}>
          <svg {...common}>
            <path d="M18 23a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z" fill={preset.soft} stroke={stroke} strokeWidth="2.4" />
            <path d="M7 39c1.2-7 5.4-11 11-11s9.8 4 11 11H7Z" fill="#fff" stroke={stroke} strokeWidth="2.4" />
            <path d="M33 15h8v20h-8V15Z" fill="#fff" stroke={stroke} strokeWidth="2.4" />
            <path d="M36 21h2M36 27h2" stroke={stroke} strokeWidth="2.3" strokeLinecap="round" />
          </svg>
        </IconWrapper>
      );
    case "expense":
      return (
        <IconWrapper preset={preset} large={large}>
          <svg {...common}>
            <path d="M13 10h22v28H13V10Z" fill="#fff" stroke={stroke} strokeWidth="2.4" />
            <path d="M18 18h12M18 24h12M18 30h7" stroke={stroke} strokeWidth="2.3" strokeLinecap="round" />
            <path d="M33 33 41 25M34 25h7v7" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </IconWrapper>
      );
    case "report":
      return (
        <IconWrapper preset={preset} large={large}>
          <svg {...common}>
            <path d="M10 38h28" stroke={stroke} strokeWidth="2.6" strokeLinecap="round" />
            <path d="M14 25h6v13h-6V25ZM22 16h6v22h-6V16ZM30 10h6v28h-6V10Z" fill={preset.soft} stroke={stroke} strokeWidth="2.3" />
            <path d="M13 17 22 9l8 5 7-8" stroke={stroke} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </IconWrapper>
      );
    case "gas":
      return (
        <IconWrapper preset={preset} large={large}>
          <svg {...common}>
            <path d="M14 9h17v32H14V9Z" fill="#fff" stroke={stroke} strokeWidth="2.4" />
            <path d="M18 14h9v8h-9v-8Z" fill={preset.soft} stroke={stroke} strokeWidth="2.2" />
            <path d="M31 17h4l4 5v12a4 4 0 0 1-8 0" stroke={stroke} strokeWidth="2.4" strokeLinecap="round" />
            <path d="M18 32h9" stroke={stroke} strokeWidth="2.4" strokeLinecap="round" />
          </svg>
        </IconWrapper>
      );
    case "legal":
      return (
        <IconWrapper preset={preset} large={large}>
          <svg {...common}>
            <path d="M24 8v32M14 14h20" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" />
            <path d="M14 14 8 26h12l-6-12ZM34 14l-6 12h12l-6-12Z" fill={preset.soft} stroke={stroke} strokeWidth="2.2" strokeLinejoin="round" />
            <path d="M18 40h12" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </IconWrapper>
      );
    case "settings":
      return (
        <IconWrapper preset={preset} large={large}>
          <svg {...common}>
            <path d="M24 31a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z" fill="#fff" stroke={stroke} strokeWidth="2.4" />
            <path d="M39 26v-4l-5-1.4a11.7 11.7 0 0 0-1.1-2.6l2.6-4.5-3-3-4.5 2.6c-.8-.5-1.7-.8-2.6-1.1L24 7h-4l-1.4 5c-.9.3-1.8.6-2.6 1.1l-4.5-2.6-3 3 2.6 4.5c-.5.8-.8 1.7-1.1 2.6L5 22v4l5 1.4c.3.9.6 1.8 1.1 2.6l-2.6 4.5 3 3 4.5-2.6c.8.5 1.7.8 2.6 1.1L20 41h4l1.4-5c.9-.3 1.8-.6 2.6-1.1l4.5 2.6 3-3-2.6-4.5c.5-.8.8-1.7 1.1-2.6L39 26Z" fill={preset.soft} stroke={stroke} strokeWidth="2.1" strokeLinejoin="round" />
          </svg>
        </IconWrapper>
      );
    default:
      return (
        <IconWrapper preset={preset} large={large}>
          <svg {...common}>
            <path d="M12 13a5 5 0 0 1 5-5h14a5 5 0 0 1 5 5v22a5 5 0 0 1-5 5H17a5 5 0 0 1-5-5V13Z" fill="#fff" stroke={stroke} strokeWidth="2.4" />
            <path d="M18 17h12M18 24h12M18 31h8" stroke={stroke} strokeWidth="2.3" strokeLinecap="round" />
          </svg>
        </IconWrapper>
      );
  }
};

const getParticleSet = (preset) => {
  const sets = {
    lottery: [
      { icon: "ticket", color: "#7c3aed", bg: "#f3e8ff", border: "#c4b5fd" },
      { icon: "ticketAlt", color: "#db2777", bg: "#fce7f3", border: "#f9a8d4" },
      { icon: "star", color: "#ca8a04", bg: "#fef3c7", border: "#fde68a" },
      { icon: "coin", color: "#0891b2", bg: "#cffafe", border: "#67e8f9" },
      { icon: "gift", color: "#16a34a", bg: "#dcfce7", border: "#86efac" },
      { icon: "spark", color: "#9333ea", bg: "#faf5ff", border: "#d8b4fe" },
    ],
    inventory: [
      { icon: "box", color: "#2563eb", bg: "#dbeafe", border: "#93c5fd" },
      { icon: "tag", color: "#059669", bg: "#d1fae5", border: "#6ee7b7" },
      { icon: "barcode", color: "#475569", bg: "#f1f5f9", border: "#cbd5e1" },
      { icon: "carton", color: "#ea580c", bg: "#ffedd5", border: "#fdba74" },
      { icon: "shelf", color: "#7c3aed", bg: "#f3e8ff", border: "#c4b5fd" },
      { icon: "check", color: "#16a34a", bg: "#dcfce7", border: "#86efac" },
    ],
    pos: [
      { icon: "cart", color: "#0891b2", bg: "#cffafe", border: "#67e8f9" },
      { icon: "rupee", color: "#059669", bg: "#d1fae5", border: "#6ee7b7" },
      { icon: "receipt", color: "#2563eb", bg: "#dbeafe", border: "#93c5fd" },
      { icon: "card", color: "#7c3aed", bg: "#f3e8ff", border: "#c4b5fd" },
      { icon: "bolt", color: "#ca8a04", bg: "#fef3c7", border: "#fde68a" },
      { icon: "scanner", color: "#475569", bg: "#f1f5f9", border: "#cbd5e1" },
    ],
    sales: [
      { icon: "receipt", color: "#059669", bg: "#d1fae5", border: "#6ee7b7" },
      { icon: "rupee", color: "#16a34a", bg: "#dcfce7", border: "#86efac" },
      { icon: "chartUp", color: "#2563eb", bg: "#dbeafe", border: "#93c5fd" },
      { icon: "card", color: "#7c3aed", bg: "#f3e8ff", border: "#c4b5fd" },
      { icon: "bag", color: "#ea580c", bg: "#ffedd5", border: "#fdba74" },
      { icon: "percent", color: "#db2777", bg: "#fce7f3", border: "#f9a8d4" },
    ],
    payroll: [
      { icon: "user", color: "#ea580c", bg: "#ffedd5", border: "#fdba74" },
      { icon: "group", color: "#2563eb", bg: "#dbeafe", border: "#93c5fd" },
      { icon: "rupee", color: "#059669", bg: "#d1fae5", border: "#6ee7b7" },
      { icon: "clock", color: "#7c3aed", bg: "#f3e8ff", border: "#c4b5fd" },
      { icon: "calendar", color: "#db2777", bg: "#fce7f3", border: "#f9a8d4" },
      { icon: "document", color: "#475569", bg: "#f1f5f9", border: "#cbd5e1" },
    ],
    expense: [
      { icon: "bill", color: "#dc2626", bg: "#fee2e2", border: "#fecaca" },
      { icon: "arrowDown", color: "#ea580c", bg: "#ffedd5", border: "#fdba74" },
      { icon: "rupee", color: "#059669", bg: "#d1fae5", border: "#6ee7b7" },
      { icon: "wallet", color: "#7c3aed", bg: "#f3e8ff", border: "#c4b5fd" },
      { icon: "calendar", color: "#2563eb", bg: "#dbeafe", border: "#93c5fd" },
      { icon: "document", color: "#475569", bg: "#f1f5f9", border: "#cbd5e1" },
    ],
    report: [
      { icon: "chartBar", color: "#4f46e5", bg: "#e0e7ff", border: "#a5b4fc" },
      { icon: "chartUp", color: "#059669", bg: "#d1fae5", border: "#6ee7b7" },
      { icon: "pie", color: "#db2777", bg: "#fce7f3", border: "#f9a8d4" },
      { icon: "trend", color: "#2563eb", bg: "#dbeafe", border: "#93c5fd" },
      { icon: "rupee", color: "#ca8a04", bg: "#fef3c7", border: "#fde68a" },
      { icon: "document", color: "#475569", bg: "#f1f5f9", border: "#cbd5e1" },
    ],
    gas: [
      { icon: "fuel", color: "#ca8a04", bg: "#fef3c7", border: "#fde68a" },
      { icon: "flame", color: "#dc2626", bg: "#fee2e2", border: "#fecaca" },
      { icon: "meter", color: "#2563eb", bg: "#dbeafe", border: "#93c5fd" },
      { icon: "receipt", color: "#059669", bg: "#d1fae5", border: "#6ee7b7" },
      { icon: "rupee", color: "#16a34a", bg: "#dcfce7", border: "#86efac" },
      { icon: "drop", color: "#0891b2", bg: "#cffafe", border: "#67e8f9" },
    ],
    legal: [
      { icon: "scale", color: "#475569", bg: "#f1f5f9", border: "#cbd5e1" },
      { icon: "document", color: "#2563eb", bg: "#dbeafe", border: "#93c5fd" },
      { icon: "pen", color: "#7c3aed", bg: "#f3e8ff", border: "#c4b5fd" },
      { icon: "shield", color: "#059669", bg: "#d1fae5", border: "#6ee7b7" },
      { icon: "check", color: "#16a34a", bg: "#dcfce7", border: "#86efac" },
      { icon: "briefcase", color: "#ca8a04", bg: "#fef3c7", border: "#fde68a" },
    ],
    settings: [
      { icon: "gear", color: "#0f766e", bg: "#ccfbf1", border: "#5eead4" },
      { icon: "lock", color: "#2563eb", bg: "#dbeafe", border: "#93c5fd" },
      { icon: "shield", color: "#059669", bg: "#d1fae5", border: "#6ee7b7" },
      { icon: "slider", color: "#7c3aed", bg: "#f3e8ff", border: "#c4b5fd" },
      { icon: "user", color: "#ea580c", bg: "#ffedd5", border: "#fdba74" },
      { icon: "check", color: "#16a34a", bg: "#dcfce7", border: "#86efac" },
    ],
    default: [
      { icon: "document", color: "#334155", bg: "#f1f5f9", border: "#cbd5e1" },
      { icon: "star", color: "#ca8a04", bg: "#fef3c7", border: "#fde68a" },
      { icon: "bolt", color: "#0891b2", bg: "#cffafe", border: "#67e8f9" },
      { icon: "check", color: "#16a34a", bg: "#dcfce7", border: "#86efac" },
      { icon: "spark", color: "#7c3aed", bg: "#f3e8ff", border: "#c4b5fd" },
      { icon: "arrowRight", color: "#2563eb", bg: "#dbeafe", border: "#93c5fd" },
    ],
  };

  return sets[preset.type] || sets.default;
};

const ParticleSvg = ({ icon, color }) => {
  const common = {
    width: 30,
    height: 30,
    viewBox: "0 0 32 32",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
  };

  switch (icon) {
    case "ticket":
      return <svg {...common}><path d="M5 12.5 20 5l7 13.5-15 7.5L5 12.5Z" fill="currentColor" opacity=".16"/><path d="M5 12.5 20 5l7 13.5-15 7.5L5 12.5Z" stroke={color} strokeWidth="2.1"/><path d="M11 13.2 20 8.7M13.2 18l8.2-4.2" stroke={color} strokeWidth="1.8" strokeLinecap="round"/></svg>;
    case "ticketAlt":
      return <svg {...common}><path d="M6 9h20v14H6V9Z" fill="currentColor" opacity=".14"/><path d="M6 9h20v14H6V9Z" stroke={color} strokeWidth="2"/><path d="M12 14h8M12 18h5" stroke={color} strokeWidth="1.8" strokeLinecap="round"/></svg>;
    case "box":
    case "carton":
      return <svg {...common}><path d="M6 11.5 16 6l10 5.5-10 5.5-10-5.5Z" fill="currentColor" opacity=".14"/><path d="M6 11.5 16 6l10 5.5-10 5.5-10-5.5Z" stroke={color} strokeWidth="2"/><path d="M6 11.5v10.8L16 28V17M26 11.5v10.8L16 28" stroke={color} strokeWidth="2" strokeLinejoin="round"/></svg>;
    case "tag":
      return <svg {...common}><path d="M6 7h11l9 9-10 10-9-9V7Z" fill="currentColor" opacity=".14"/><path d="M6 7h11l9 9-10 10-9-9V7Z" stroke={color} strokeWidth="2" strokeLinejoin="round"/><path d="M12 12h.1" stroke={color} strokeWidth="4" strokeLinecap="round"/></svg>;
    case "barcode":
      return <svg {...common}><path d="M6 8h20v16H6V8Z" fill="currentColor" opacity=".12"/><path d="M9 11v10M13 11v10M16 11v10M21 11v10M24 11v10" stroke={color} strokeWidth="2" strokeLinecap="round"/></svg>;
    case "shelf":
      return <svg {...common}><path d="M7 9h18v5H7V9ZM7 18h18v5H7v-5Z" fill="currentColor" opacity=".14"/><path d="M7 9h18v5H7V9ZM7 18h18v5H7v-5Z" stroke={color} strokeWidth="2"/><path d="M10 27h12" stroke={color} strokeWidth="2" strokeLinecap="round"/></svg>;
    case "cart":
      return <svg {...common}><path d="M7 8h16l2.5 12.5H10L7 8Z" fill="currentColor" opacity=".14"/><path d="M7 8h16l2.5 12.5H10L7 8ZM6 8H4M11 25.5h.1M23 25.5h.1" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case "rupee":
      return <svg {...common}><path d="M11 7h11M11 12h11M12 7h4.8a5 5 0 0 1 0 10H12l9 9" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case "receipt":
    case "bill":
      return <svg {...common}><path d="M9 5h14v22l-3-2-3 2-3-2-3 2-2-1.4V5Z" fill="currentColor" opacity=".12"/><path d="M9 5h14v22l-3-2-3 2-3-2-3 2-2-1.4V5Z" stroke={color} strokeWidth="2"/><path d="M13 11h7M13 16h7M13 21h4" stroke={color} strokeWidth="1.8" strokeLinecap="round"/></svg>;
    case "card":
      return <svg {...common}><path d="M5 9h22v14H5V9Z" fill="currentColor" opacity=".13"/><path d="M5 9h22v14H5V9Z" stroke={color} strokeWidth="2"/><path d="M5 14h22M10 19h5" stroke={color} strokeWidth="1.8" strokeLinecap="round"/></svg>;
    case "bolt":
      return <svg {...common}><path d="m18 4-9 14h7l-2 10 9-14h-7l2-10Z" fill="currentColor" opacity=".18"/><path d="m18 4-9 14h7l-2 10 9-14h-7l2-10Z" stroke={color} strokeWidth="2" strokeLinejoin="round"/></svg>;
    case "scanner":
      return <svg {...common}><path d="M7 10V7h5M20 7h5v3M25 22v3h-5M12 25H7v-3" stroke={color} strokeWidth="2" strokeLinecap="round"/><path d="M10 16h12" stroke={color} strokeWidth="2.2" strokeLinecap="round"/></svg>;
    case "chartUp":
    case "trend":
      return <svg {...common}><path d="M6 24h20" stroke={color} strokeWidth="2.2" strokeLinecap="round"/><path d="M8 21 14 15l5 4 7-10" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 9h4v4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case "chartBar":
      return <svg {...common}><path d="M7 25h18" stroke={color} strokeWidth="2.2" strokeLinecap="round"/><path d="M10 17h3v8h-3v-8ZM15 11h3v14h-3V11ZM20 7h3v18h-3V7Z" fill="currentColor" opacity=".14" stroke={color} strokeWidth="1.8"/></svg>;
    case "pie":
      return <svg {...common}><path d="M16 6v10h10A10 10 0 0 0 16 6Z" fill="currentColor" opacity=".18" stroke={color} strokeWidth="2"/><path d="M22.5 19.5A9 9 0 1 1 13 7.5v9h9.5Z" stroke={color} strokeWidth="2"/></svg>;
    case "bag":
      return <svg {...common}><path d="M8 12h16l-1 15H9L8 12Z" fill="currentColor" opacity=".14"/><path d="M8 12h16l-1 15H9L8 12Z" stroke={color} strokeWidth="2"/><path d="M12 12a4 4 0 0 1 8 0" stroke={color} strokeWidth="2" strokeLinecap="round"/></svg>;
    case "percent":
      return <svg {...common}><path d="M9 23 23 9M10 11h.1M22 21h.1" stroke={color} strokeWidth="3" strokeLinecap="round"/></svg>;
    case "user":
      return <svg {...common}><path d="M16 15a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z" fill="currentColor" opacity=".14" stroke={color} strokeWidth="2"/><path d="M6 27c1.2-6 4.8-9 10-9s8.8 3 10 9H6Z" stroke={color} strokeWidth="2" strokeLinejoin="round"/></svg>;
    case "group":
      return <svg {...common}><path d="M12 15a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM5 27c1-5 3.4-8 7-8s6 3 7 8H5ZM18 26c.6-3.4 2.2-5.4 4.7-5.4 2.4 0 4 2 4.7 5.4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case "clock":
      return <svg {...common}><path d="M16 27a11 11 0 1 0 0-22 11 11 0 0 0 0 22Z" fill="currentColor" opacity=".12" stroke={color} strokeWidth="2"/><path d="M16 10v7l5 3" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case "calendar":
      return <svg {...common}><path d="M7 9h18v17H7V9Z" fill="currentColor" opacity=".12" stroke={color} strokeWidth="2"/><path d="M11 6v5M21 6v5M7 14h18" stroke={color} strokeWidth="2" strokeLinecap="round"/></svg>;
    case "document":
      return <svg {...common}><path d="M10 5h9l5 5v17H10V5Z" fill="currentColor" opacity=".12" stroke={color} strokeWidth="2" strokeLinejoin="round"/><path d="M18 5v6h6M14 16h6M14 21h5" stroke={color} strokeWidth="1.8" strokeLinecap="round"/></svg>;
    case "wallet":
      return <svg {...common}><path d="M6 10h20v15H6V10Z" fill="currentColor" opacity=".13" stroke={color} strokeWidth="2"/><path d="M21 15h6v6h-6a3 3 0 0 1 0-6Z" fill="#fff" stroke={color} strokeWidth="2"/></svg>;
    case "arrowDown":
      return <svg {...common}><path d="M16 5v20M8 17l8 8 8-8" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case "fuel":
      return <svg {...common}><path d="M9 5h12v22H9V5Z" fill="currentColor" opacity=".12" stroke={color} strokeWidth="2"/><path d="M12 9h6v6h-6V9ZM21 10h3l3 4v8a3 3 0 0 1-6 0" stroke={color} strokeWidth="2" strokeLinecap="round"/></svg>;
    case "flame":
      return <svg {...common}><path d="M16 28c5 0 8-3.4 8-8.2 0-3.6-2.1-6.4-5-8.8-.5 2.8-1.9 4.1-3.4 5.4.2-4.3-1.4-7.1-4.1-9.4.2 5.6-4.5 7.3-4.5 12.7C7 24.5 11 28 16 28Z" fill="currentColor" opacity=".16" stroke={color} strokeWidth="2" strokeLinejoin="round"/></svg>;
    case "meter":
      return <svg {...common}><path d="M7 20a9 9 0 0 1 18 0" stroke={color} strokeWidth="2" strokeLinecap="round"/><path d="M16 20l6-6M9 24h14" stroke={color} strokeWidth="2" strokeLinecap="round"/></svg>;
    case "drop":
      return <svg {...common}><path d="M16 5s8 8.4 8 14a8 8 0 0 1-16 0c0-5.6 8-14 8-14Z" fill="currentColor" opacity=".13" stroke={color} strokeWidth="2" strokeLinejoin="round"/></svg>;
    case "scale":
      return <svg {...common}><path d="M16 5v22M9 10h14" stroke={color} strokeWidth="2" strokeLinecap="round"/><path d="M9 10 5 19h8l-4-9ZM23 10l-4 9h8l-4-9Z" fill="currentColor" opacity=".12" stroke={color} strokeWidth="1.8" strokeLinejoin="round"/></svg>;
    case "pen":
      return <svg {...common}><path d="M8 24l2-6 11-11 4 4-11 11-6 2Z" fill="currentColor" opacity=".14" stroke={color} strokeWidth="2" strokeLinejoin="round"/><path d="M19 9l4 4" stroke={color} strokeWidth="2" strokeLinecap="round"/></svg>;
    case "shield":
      return <svg {...common}><path d="M16 4 25 8v7c0 6-3.7 10.5-9 13-5.3-2.5-9-7-9-13V8l9-4Z" fill="currentColor" opacity=".13" stroke={color} strokeWidth="2" strokeLinejoin="round"/><path d="m12 16 3 3 6-7" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case "briefcase":
      return <svg {...common}><path d="M7 12h18v13H7V12Z" fill="currentColor" opacity=".12" stroke={color} strokeWidth="2"/><path d="M13 12V8h6v4M7 17h18" stroke={color} strokeWidth="2" strokeLinecap="round"/></svg>;
    case "gear":
      return <svg {...common}><path d="M16 20a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" stroke={color} strokeWidth="2"/><path d="m26 18 .7-2.5-2.8-1c-.2-.6-.4-1.2-.8-1.8l1.3-2.6-2.5-2.5-2.6 1.3c-.6-.3-1.2-.6-1.8-.8L15.5 5h-3l-.9 2.6c-.6.2-1.2.4-1.8.8L7.1 7.1 4.6 9.6l1.3 2.6c-.3.6-.6 1.2-.8 1.8l-2.8 1 .7 2.5 2.8 1c.2.6.4 1.2.8 1.8l-1.3 2.6 2.5 2.5 2.6-1.3c.6.3 1.2.6 1.8.8l.9 2.6h3l.9-2.6c.6-.2 1.2-.4 1.8-.8l2.6 1.3 2.5-2.5-1.3-2.6c.3-.6.6-1.2.8-1.8l2.8-1Z" fill="currentColor" opacity=".12" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/></svg>;
    case "lock":
      return <svg {...common}><path d="M9 14h14v12H9V14Z" fill="currentColor" opacity=".12" stroke={color} strokeWidth="2"/><path d="M12 14v-3a4 4 0 0 1 8 0v3M16 19v3" stroke={color} strokeWidth="2" strokeLinecap="round"/></svg>;
    case "slider":
      return <svg {...common}><path d="M6 10h20M6 16h20M6 22h20" stroke={color} strokeWidth="2" strokeLinecap="round"/><path d="M12 7v6M21 13v6M15 19v6" stroke={color} strokeWidth="2" strokeLinecap="round"/></svg>;
    case "star":
      return <svg {...common}><path d="m16 5 3.1 6.3 7 .9-5.1 4.9 1.3 6.9-6.3-3.3L9.7 25l1.3-6.9-5.1-4.9 7-.9L16 5Z" fill="currentColor" opacity=".16" stroke={color} strokeWidth="2" strokeLinejoin="round"/></svg>;
    case "coin":
      return <svg {...common}><path d="M16 27a11 11 0 1 0 0-22 11 11 0 0 0 0 22Z" fill="currentColor" opacity=".14" stroke={color} strokeWidth="2"/><path d="M16 9v14M12 12h6a2.5 2.5 0 0 1 0 5h-4" stroke={color} strokeWidth="2" strokeLinecap="round"/></svg>;
    case "gift":
      return <svg {...common}><path d="M7 13h18v14H7V13Z" fill="currentColor" opacity=".13" stroke={color} strokeWidth="2"/><path d="M6 13h20M16 13v14M11 10a3 3 0 0 0 5 3 3 3 0 1 0-5-3ZM21 10a3 3 0 0 1-5 3 3 3 0 1 1 5-3Z" stroke={color} strokeWidth="2" strokeLinecap="round"/></svg>;
    case "spark":
      return <svg {...common}><path d="M16 4l2.2 8.2L26 15l-7.8 2.8L16 26l-2.2-8.2L6 15l7.8-2.8L16 4Z" fill="currentColor" opacity=".16" stroke={color} strokeWidth="2" strokeLinejoin="round"/></svg>;
    case "check":
      return <svg {...common}><path d="m8 16 5 5 11-11" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case "arrowRight":
      return <svg {...common}><path d="M6 16h19M18 9l7 7-7 7" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    default:
      return <svg {...common}><path d="M16 5 27 16 16 27 5 16 16 5Z" fill="currentColor" opacity=".14" stroke={color} strokeWidth="2"/></svg>;
  }
};

const MiniParticle = ({ preset, index }) => {
  const particles = getParticleSet(preset);
  const particle = particles[index - 1] || particles[0];

  return (
    <span
      className={`pv4-particle pv4-particle-${index}`}
      style={{
        "--pv4-particle-color": particle.color,
        "--pv4-particle-bg": particle.bg,
        "--pv4-particle-border": particle.border,
      }}
    >
      <ParticleSvg icon={particle.icon} color={particle.color} />
    </span>
  );
};

function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const launchTimerRef = useRef(null);
  const { handleToast } = useToastHandler();
  const { permissions, userRole: contextUserRole, user } = useContext(AuthContext);
  const { can: canAccessModule, isConfigured: hasPermissionGroupConfigured } = useMyPermissions();

  const [allowedFeatures, setAllowedFeatures] = useState([]);
  const [appCatalog, setAppCatalog] = useState([]);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("authToken") || "null");
    axios.get(`${config.api.host}${config.api.featuresWithPrices}`, {
      headers: stored?.access ? { Authorization: `Bearer ${stored.access}` } : {},
    }).then(({ data }) => setAppCatalog(Array.isArray(data) ? data : data?.results || []))
      .catch(() => setAppCatalog([]));
  }, []);
  const [isLoading, setIsLoading] = useState(true);
  const [showDefaultRadio, setShowDefaultRadio] = useState(false);
  const [selectedDefaultApp, setSelectedDefaultApp] = useState(null);
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [launchingApp, setLaunchingApp] = useState(null);

  const formatRoleName = (roleName) => {
    if (!roleName) return "Store Owner";

    return String(roleName)
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getUserDisplayName = () => {
    return (
      user?.full_name ||
      user?.name ||
      user?.store_name ||
      user?.username ||
      "Welcome"
    );
  };

  const getUserInitials = () => {
    const displayName = getUserDisplayName();

    return (
      String(displayName)
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("") || "PV"
    );
  };

  const checkPermission = (permissionCode) => {
    if (!permissions || !permissionCode) return true;

    if (permissions.includes("all")) {
      return true;
    }

    if (permissionCode && permissionCode.startsWith("PERM_LEGAL")) {
      return true;
    }

    return permissions.includes(permissionCode);
  };

  const canAccessMenuItem = (menuItem) => {
    if (menuItem.permissions && !checkPermission(menuItem.permissions)) {
      return false;
    }

    if (
      menuItem.access &&
      !menuItem.access.includes("all") &&
      !menuItem.access.includes(userRole)
    ) {
      return false;
    }

    return true;
  };

  const getFirstAccessibleRoute = (appKey) => {
    const menuItems = getMenuItemsForApp(appKey);

    for (const item of menuItems) {
      if (item.submenu) {
        const accessibleSubItem = item.submenu.find((subItem) =>
          canAccessMenuItem(subItem)
        );

        if (accessibleSubItem) {
          return accessibleSubItem.path;
        }
      } else if (item.path && canAccessMenuItem(item)) {
        return item.path;
      }
    }

    return null;
  };

  const getAccessibleRouteForApp = (appKey, initialRoute) => {
    const menuItems = getMenuItemsForApp(appKey);
    const initialMenuItem = menuItems.find(
      (item) =>
        item.path === initialRoute ||
        (item.submenu &&
          item.submenu.some((subItem) => subItem.path === initialRoute))
    );

    let targetRoute = initialRoute;

    if (initialMenuItem) {
      if (initialMenuItem.submenu) {
        const initialSubItem = initialMenuItem.submenu.find(
          (subItem) => subItem.path === initialRoute
        );

        if (initialSubItem && !canAccessMenuItem(initialSubItem)) {
          targetRoute = getFirstAccessibleRoute(appKey);
        }
      } else if (!canAccessMenuItem(initialMenuItem)) {
        targetRoute = getFirstAccessibleRoute(appKey);
      }
    } else {
      targetRoute = getFirstAccessibleRoute(appKey);
    }

    return targetRoute;
  };

  useEffect(() => {
    return () => {
      if (launchTimerRef.current) {
        clearTimeout(launchTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (
      selectedDefaultApp &&
      !isSettingDefault &&
      location.pathname === "/" &&
      allowedFeatures.length > 0
    ) {
      const app = getFilteredApps(allowedFeatures, permissions, { isConfigured: hasPermissionGroupConfigured, can: canAccessModule }).find(
        (item) => item.key === selectedDefaultApp
      );

      if (app) {
        const route = getAccessibleRouteForApp(selectedDefaultApp, app.initialRoute);

        if (route) {
          navigate(route);
        } else {
          handleToast({
            type: "ERROR",
            message:
              "You don't have permission to access any features of your default app",
          });
        }
      }
    }
  }, [
    selectedDefaultApp,
    allowedFeatures,
    navigate,
    isSettingDefault,
    location.pathname,
  ]);

  useEffect(() => {
    const updateFeaturesFromStorage = () => {
      try {
        setIsLoading(true);

        const storedFeatures = localStorage.getItem("allowedFeatures");
        if (storedFeatures) {
          const parsedFeatures = JSON.parse(storedFeatures);
          setAllowedFeatures(Array.isArray(parsedFeatures) ? parsedFeatures : []);
        } else {
          setAllowedFeatures([]);
        }

        const defaultApp = localStorage.getItem("defaultApp");
        if (defaultApp) {
          setSelectedDefaultApp(defaultApp);
        }

        if (user && user.ms_role_name) {
          setUserRole(formatRoleName(user.ms_role_name));
        } else if (contextUserRole) {
          setUserRole(contextUserRole);
        } else {
          try {
            const authTokenRaw = localStorage.getItem("authToken");
            const authToken = authTokenRaw ? JSON.parse(authTokenRaw) : null;

            if (authToken && authToken.role_name) {
              setUserRole(formatRoleName(authToken.role_name));
            } else {
              setUserRole("Store Owner");
            }
          } catch (error) {
            setUserRole("Store Owner");
          }
        }
      } catch (error) {
        console.error("Error parsing allowedFeatures from localStorage:", error);
        setAllowedFeatures([]);
      } finally {
        setIsLoading(false);
      }
    };

    updateFeaturesFromStorage();

    const handleFeaturesUpdated = () => {
      updateFeaturesFromStorage();
    };

    window.addEventListener("allowedFeaturesUpdated", handleFeaturesUpdated);

    return () => {
      window.removeEventListener("allowedFeaturesUpdated", handleFeaturesUpdated);
    };
  }, [user, contextUserRole]);

  const allAvailableApps = useMemo(() => {
    if (isLoading) return [];

    return applyCatalogLayout(getFilteredApps(allowedFeatures, permissions, { isConfigured: hasPermissionGroupConfigured, can: canAccessModule }).filter(
      (app) => app.key !== "paisa_vara"
    ), appCatalog);
  }, [allowedFeatures, permissions, isLoading, appCatalog, hasPermissionGroupConfigured, canAccessModule]);

  const filteredApps = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) return allAvailableApps;

    return allAvailableApps.filter((app) => {
      return (
        String(app.displayName || "").toLowerCase().includes(query) ||
        String(app.name || "").toLowerCase().includes(query) ||
        String(app.key || "").toLowerCase().includes(query)
      );
    });
  }, [allAvailableApps, searchQuery]);

  const selectedDefaultAppName = useMemo(() => {
    if (!selectedDefaultApp) return "";

    const selectedApp = allAvailableApps.find((app) => app.key === selectedDefaultApp);
    return selectedApp?.displayName || selectedDefaultApp;
  }, [selectedDefaultApp, allAvailableApps]);

  const handleAppClick = (app) => {
    const targetRoute = getEnhancedInitialRoute(
      app.key,
      allowedFeatures,
      permissions,
      userRole
    );

    if (!targetRoute) {
      handleToast({
        type: "ERROR",
        message: "You don't have permission to access any features of this app",
      });
      return;
    }

    const preset = getVisualPreset(app);

    setLaunchingApp({
      appKey: app.key,
      appName: app.displayName || app.name || app.key,
      targetRoute,
      preset,
    });

    if (launchTimerRef.current) {
      clearTimeout(launchTimerRef.current);
    }

    launchTimerRef.current = setTimeout(() => {
      localStorage.setItem("currentApp", app.key);

      window.dispatchEvent(
        new CustomEvent("appSwitched", {
          detail: {
            appKey: app.key,
            targetRoute,
          },
        })
      );

      navigate(targetRoute);
    }, 820);
  };

  const handleDefaultPathClick = () => {
    setShowDefaultRadio((prev) => !prev);
  };

  const handleDefaultAppSelection = (appKey) => {
    setIsSettingDefault(true);
    setSelectedDefaultApp(appKey);
    localStorage.setItem("defaultApp", appKey);
    window.dispatchEvent(new Event("defaultAppUpdated"));

    const selectedApp = getFilteredApps(allowedFeatures, permissions, { isConfigured: hasPermissionGroupConfigured, can: canAccessModule }).find(
      (app) => app.key === appKey
    );
    const appName = selectedApp?.displayName || appKey;

    Swal.fire({
      title: "Default App Updated",
      text: `${appName} has been set as your default app.`,
      icon: "success",
      timer: 1900,
      showConfirmButton: false,
      customClass: {
        popup: "rounded-popup",
      },
      didOpen: () => {
        const popup = Swal.getPopup();
        if (popup) {
          popup.style.borderRadius = "18px";
          popup.style.boxShadow = "0 18px 45px rgba(15, 23, 42, 0.22)";
        }
      },
    });

    setShowDefaultRadio(false);

    setTimeout(() => {
      setIsSettingDefault(false);
    }, 2400);
  };

  const handleClearDefault = () => {
    setSelectedDefaultApp(null);
    localStorage.removeItem("defaultApp");
    window.dispatchEvent(new Event("defaultAppUpdated"));

    Swal.fire({
      title: "Default App Cleared",
      text: "No default app is selected now.",
      icon: "success",
      timer: 1800,
      showConfirmButton: false,
      customClass: {
        popup: "rounded-popup",
      },
      didOpen: () => {
        const popup = Swal.getPopup();
        if (popup) {
          popup.style.borderRadius = "18px";
          popup.style.boxShadow = "0 18px 45px rgba(15, 23, 42, 0.22)";
        }
      },
    });
  };

  const renderLoadingCards = () => (
    <div className="pv4-icon-grid">
      {Array.from({ length: 12 }).map((_, index) => (
        <div className="pv4-skeleton-card" key={index}>
          <div className="pv4-skeleton-logo" />
          <div className="pv4-skeleton-line" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="responsive-box pv4-home-page">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <style>
        {`
          .pv4-home-page {
            min-height: 100vh;
            background:
              linear-gradient(135deg, rgba(2, 6, 23, 0.035) 0%, rgba(248, 250, 252, 0.94) 34%, rgba(255, 255, 255, 1) 100%);
            padding-top: 8px;
            padding-bottom: 24px;
            color: #0b1220;
          }

          .pv4-shell {
            max-width: 1040px;
            margin: 0 auto;
          }

          .pv4-hero {
            background:
              linear-gradient(135deg, #020617 0%, #0f172a 62%, #164e63 100%);
            border-radius: 20px;
            padding: 15px 17px;
            color: #ffffff;
            box-shadow: 0 16px 38px rgba(15, 23, 42, 0.18);
            position: relative;
            overflow: hidden;
            margin-bottom: 12px;
          }

          .pv4-hero-inner {
            position: relative;
            z-index: 1;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 16px;
            flex-wrap: wrap;
          }

          .pv4-kicker {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            background: rgba(255, 255, 255, 0.12);
            border: 1px solid rgba(255, 255, 255, 0.22);
            color: #e0f2fe;
            padding: 5px 10px;
            border-radius: 999px;
            font-size: 11px;
            font-weight: 950;
            margin-bottom: 8px;
          }

          .pv4-kicker-dot {
            width: 7px;
            height: 7px;
            border-radius: 999px;
            background: #38bdf8;
            box-shadow: 0 0 0 4px rgba(56, 189, 248, 0.18);
          }

          .pv4-title {
            margin: 0;
            font-size: 25px;
            line-height: 1.08;
            font-weight: 950;
            letter-spacing: -0.04em;
          }

          .pv4-profile-card {
            background: rgba(255, 255, 255, 0.11);
            border: 1px solid rgba(255, 255, 255, 0.22);
            border-radius: 16px;
            padding: 9px 11px;
            min-width: 218px;
            display: flex;
            align-items: center;
            gap: 10px;
            backdrop-filter: blur(10px);
          }

          .pv4-avatar {
            width: 38px;
            height: 38px;
            border-radius: 13px;
            background: #ffffff;
            color: #075985;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 15px;
            font-weight: 950;
            flex-shrink: 0;
          }

          .pv4-profile-name {
            font-size: 13px;
            font-weight: 950;
            color: #ffffff;
            line-height: 1.25;
          }

          .pv4-profile-role {
            margin-top: 3px;
            font-size: 11px;
            color: rgba(255, 255, 255, 0.78);
            font-weight: 750;
          }

          .pv4-toolbar {
            background: rgba(255, 255, 255, 0.96);
            border: 1px solid #cbd5e1;
            border-radius: 17px;
            padding: 11px;
            box-shadow: 0 11px 28px rgba(15, 23, 42, 0.065);
            backdrop-filter: blur(10px);
            margin-bottom: 12px;
          }

          .pv4-toolbar-row {
            display: grid;
            grid-template-columns: 1fr auto;
            gap: 10px;
            align-items: center;
          }

          .pv4-search-wrap {
            position: relative;
            min-width: 0;
          }

          .pv4-search-icon {
            position: absolute;
            left: 13px;
            top: 50%;
            transform: translateY(-50%);
            color: #64748b;
            font-size: 14px;
          }

          .pv4-search-input {
            width: 100%;
            height: 39px;
            border: 1px solid #94a3b8;
            border-radius: 999px;
            padding: 0 14px 0 38px;
            font-size: 13px;
            outline: none;
            font-weight: 750;
            color: #0b1220;
            background: #ffffff;
            transition: border 0.18s ease, box-shadow 0.18s ease;
          }

          .pv4-search-input:focus {
            border-color: #075985;
            box-shadow: 0 0 0 4px rgba(14, 116, 144, 0.16);
          }

          .pv4-actions {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            justify-content: flex-end;
          }

          .pv4-btn {
            border: none;
            border-radius: 999px;
            height: 39px;
            padding: 0 14px;
            font-size: 12px;
            font-weight: 950;
            cursor: pointer;
            transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
            white-space: nowrap;
          }

          .pv4-btn:hover {
            transform: translateY(-1px);
          }

          .pv4-btn-primary {
            background: #075985;
            color: #ffffff;
            box-shadow: 0 8px 18px rgba(7, 89, 133, 0.17);
          }

          .pv4-btn-danger {
            background: #fff7ed;
            color: #9a3412;
            border: 1px solid #fdba74;
          }

          .pv4-board-title-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
            margin: 2px 0 10px;
          }

          .pv4-board-title {
            margin: 0;
            color: #0b1220;
            font-size: 18px;
            font-weight: 950;
            letter-spacing: -0.025em;
          }

          .pv4-pills {
            display: flex;
            gap: 7px;
            flex-wrap: wrap;
          }

          .pv4-pill {
            border-radius: 999px;
            padding: 6px 9px;
            font-size: 10px;
            font-weight: 950;
            white-space: nowrap;
          }

          .pv4-pill-green {
            background: #ecfdf5;
            color: #065f46;
            border: 1px solid #6ee7b7;
          }

          .pv4-pill-blue {
            background: #eff6ff;
            color: #1e3a8a;
            border: 1px solid #93c5fd;
          }

          .pv4-pill-gray {
            background: #f8fafc;
            color: #334155;
            border: 1px solid #cbd5e1;
          }

          .pv4-default-strip {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            flex-wrap: wrap;
            background: linear-gradient(90deg, #eff6ff 0%, #ffffff 100%);
            border: 1px solid #93c5fd;
            color: #1e3a8a;
            border-radius: 14px;
            padding: 9px 12px;
            margin-bottom: 10px;
            font-size: 12px;
            font-weight: 850;
          }

          .pv4-default-strip strong {
            color: #172554;
          }

          .pv4-icon-grid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 13px;
            position: relative;
            overflow: visible;
          }

          .pv4-section-title {
            grid-column: 1 / -1;
            color: #64748b;
            font-size: 0.78rem;
            font-weight: 900;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            padding: 0.75rem 0.25rem 0.1rem;
          }

          .pv4-app-card {
            z-index: 1;
            border: 1px solid #cbd5e1;
            background: transparent;
            border-radius: 21px;
            min-height: 146px;
            padding: 16px 12px 13px;
            cursor: pointer;
            position: relative;
            overflow: visible;
            transition:
              transform 0.24s cubic-bezier(.2,.8,.2,1),
              box-shadow 0.24s cubic-bezier(.2,.8,.2,1),
              border 0.24s ease;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            isolation: isolate;
          }

          .pv4-app-card::before {
            content: "";
            position: absolute;
            inset: 0;
            z-index: 1;
            border-radius: inherit;
            background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
            pointer-events: none;
          }

          .pv4-app-card:hover {
            z-index: 50;
            transform: translateY(-6px) scale(1.025);
            box-shadow: 0 20px 46px rgba(15, 23, 42, 0.18);
            border-color: var(--pv4-accent, #075985);
          }

          .pv4-app-card.is-default {
            border-color: #075985;
          }

          .pv4-app-card.is-default::before {
            background: linear-gradient(180deg, #eff6ff 0%, #ffffff 100%);
          }

          .pv4-app-card.default-mode {
            transform: scale(0.985);
          }

          .pv4-card-shadow {
            position: absolute;
            inset: 7px;
            z-index: 0;
            border-radius: inherit;
            background: var(--pv4-soft, #f1f5f9);
            opacity: 0.34;
            transform: translateY(8px);
            transition: transform 0.24s ease, opacity 0.24s ease;
          }

          .pv4-app-card:hover .pv4-card-shadow {
            transform: translateY(13px) scale(0.94);
            opacity: 0.48;
          }

          .pv4-icon-tile {
            z-index: 4;
            width: 62px;
            height: 62px;
            border-radius: 20px;
            background: var(--pv4-soft, #f1f5f9);
            border: 1px solid var(--pv4-border, #cbd5e1);
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 9px 22px rgba(15, 23, 42, 0.075);
            position: relative;
            transition: transform 0.24s ease, box-shadow 0.24s ease;
          }

          .pv4-icon-tile-large {
            width: 88px;
            height: 88px;
            border-radius: 28px;
          }

          .pv4-logo-tile {
            z-index: 60;
            width: 64px;
            height: 64px;
            border-radius: 21px;
            background: #ffffff;
            border: 1px solid var(--pv4-border, #cbd5e1);
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.09);
            position: relative;
            transition: transform 0.24s ease, box-shadow 0.24s ease;
          }

          .pv4-app-logo {
            width: 42px;
            height: 42px;
            object-fit: contain;
            position: relative;
            z-index: 6;
          }

          .pv4-app-card:hover .pv4-logo-tile {
            transform: translateY(-3px) scale(1.05);
            box-shadow: 0 14px 30px rgba(15, 23, 42, 0.13);
          }

          .pv4-app-card:hover .pv4-icon-tile {
            transform: translateY(-3px) scale(1.05);
            box-shadow: 0 14px 30px rgba(15, 23, 42, 0.12);
          }

          .pv4-app-name {
            position: relative;
            z-index: 60;
            margin: 13px 0 0;
            font-size: 16px;
            font-weight: 950;
            color: #0b1220;
            line-height: 1.22;
            text-align: center;
            letter-spacing: -0.015em;
            max-width: 100%;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .pv4-default-tag {
            position: absolute;
            top: 9px;
            right: 9px;
            z-index: 6;
            background: #075985;
            color: #ffffff;
            border-radius: 999px;
            padding: 4px 7px;
            font-size: 9px;
            font-weight: 950;
            letter-spacing: 0.02em;
          }

          .pv4-radio-wrap {
            position: absolute;
            top: 9px;
            right: 9px;
            z-index: 6;
            width: 28px;
            height: 28px;
            border-radius: 999px;
            background: rgba(255,255,255,0.96);
            border: 1px solid #94a3b8;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .pv4-radio-wrap input {
            width: 17px;
            height: 17px;
            cursor: pointer;
            accent-color: #075985;
          }

          .pv4-launch-hint {
            position: absolute;
            z-index: 70;
            left: 10px;
            top: 10px;
            transform: translate(-8px, -8px) scale(0.92);
            opacity: 0;
            color: #ffffff;
            background:
              linear-gradient(115deg, #075985 0%, #0ea5e9 34%, #22c55e 62%, #075985 100%);
            background-size: 260% 100%;
            border: 1px solid rgba(255, 255, 255, 0.74);
            border-radius: 999px;
            font-size: 10px;
            font-weight: 950;
            padding: 5px 10px;
            transition: transform 0.22s ease, opacity 0.22s ease;
            white-space: nowrap;
            box-shadow:
              0 10px 22px rgba(14, 165, 233, 0.28),
              0 0 0 0 rgba(14, 165, 233, 0.42);
            animation: pv4-launch-chip-gradient 1.25s linear infinite, pv4-launch-chip-pulse 1.45s ease-in-out infinite;
          }

          .pv4-app-card:hover .pv4-launch-hint {
            opacity: 1;
            transform: translate(0, 0) scale(1);
          }

          @keyframes pv4-launch-chip-gradient {
            0% {
              background-position: 0% 50%;
            }
            100% {
              background-position: 260% 50%;
            }
          }

          @keyframes pv4-launch-chip-pulse {
            0%, 100% {
              box-shadow:
                0 10px 22px rgba(14, 165, 233, 0.28),
                0 0 0 0 rgba(14, 165, 233, 0.34);
            }
            50% {
              box-shadow:
                0 12px 28px rgba(34, 197, 94, 0.30),
                0 0 0 7px rgba(14, 165, 233, 0.08);
            }
          }

          .pv4-hover-scene {
            position: absolute;
            inset: -122px;
            pointer-events: none;
            z-index: 40;
            opacity: 0;
            transition: opacity 0.18s ease;
          }

          .pv4-app-card:hover .pv4-hover-scene {
            opacity: 1;
          }

          .pv4-particle {
            position: absolute;
            z-index: 41;
            left: 50%;
            top: 50%;
            width: 44px;
            height: 44px;
            border-radius: 16px;
            background: var(--pv4-particle-bg, #f1f5f9);
            border: 1px solid var(--pv4-particle-border, #cbd5e1);
            color: var(--pv4-particle-color, #075985);
            box-shadow:
              0 20px 34px color-mix(in srgb, var(--pv4-particle-color, #075985) 36%, transparent),
              0 10px 20px rgba(15, 23, 42, 0.20),
              0 0 0 4px color-mix(in srgb, var(--pv4-particle-color, #075985) 12%, transparent);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            transform: translate(-50%, -50%) scale(0.25);
            opacity: 0;
          }

          .pv4-particle svg {
            width: 30px;
            height: 30px;
          }

          .pv4-app-card:hover .pv4-particle {
            animation: pv4-spring-confetti 0.88s cubic-bezier(.18, 1.55, .42, 1) forwards;
          }

          .pv4-particle-1 { animation-delay: 0.01s; }
          .pv4-particle-2 { animation-delay: 0.05s; }
          .pv4-particle-3 { animation-delay: 0.09s; }
          .pv4-particle-4 { animation-delay: 0.13s; }
          .pv4-particle-5 { animation-delay: 0.17s; }
          .pv4-particle-6 { animation-delay: 0.21s; }

          .pv4-app-card:hover .pv4-particle-1 {
            --x: -142px;
            --y: -118px;
            --r: -18deg;
          }

          .pv4-app-card:hover .pv4-particle-2 {
            --x: 142px;
            --y: -118px;
            --r: 18deg;
          }

          .pv4-app-card:hover .pv4-particle-3 {
            --x: -142px;
            --y: 118px;
            --r: 18deg;
          }

          .pv4-app-card:hover .pv4-particle-4 {
            --x: 142px;
            --y: 118px;
            --r: -18deg;
          }

          .pv4-app-card:hover .pv4-particle-5 {
            --x: 0px;
            --y: -148px;
            --r: 0deg;
          }

          .pv4-app-card:hover .pv4-particle-6 {
            --x: 0px;
            --y: 148px;
            --r: 0deg;
          }

          @keyframes pv4-spring-confetti {
            0% {
              opacity: 0;
              transform: translate(-50%, -50%) scale(0.18) rotate(0deg);
            }
            38% {
              opacity: 1;
              transform: translate(calc(-50% + var(--x) * 1.10), calc(-50% + var(--y) * 1.10)) scale(1.14) rotate(var(--r));
            }
            62% {
              opacity: 1;
              transform: translate(calc(-50% + var(--x) * 0.91), calc(-50% + var(--y) * 0.91)) scale(0.94) rotate(calc(var(--r) * -0.35));
            }
            100% {
              opacity: 1;
              transform: translate(calc(-50% + var(--x)), calc(-50% + var(--y))) scale(1.04) rotate(var(--r));
            }
          }

          .pv4-launch-overlay {
            position: fixed;
            inset: 0;
            z-index: 9999;
            background: rgba(2, 6, 23, 0.64);
            backdrop-filter: blur(12px);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px;
          }

          .pv4-launch-card {
            width: min(92vw, 320px);
            min-height: 260px;
            border-radius: 28px;
            background: #ffffff;
            border: 1px solid rgba(255,255,255,0.75);
            box-shadow: 0 35px 100px rgba(2, 6, 23, 0.38);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 28px;
            animation: pv4-launch-pop 0.82s cubic-bezier(.18, 1.35, .32, 1) forwards;
            position: relative;
            overflow: hidden;
          }

          .pv4-launch-card::before {
            content: "";
            position: absolute;
            width: 220px;
            height: 220px;
            border-radius: 999px;
            background: var(--pv4-soft, #f1f5f9);
            top: -90px;
            right: -86px;
            opacity: 0.85;
          }

          .pv4-launch-card > * {
            position: relative;
            z-index: 1;
          }

          .pv4-launch-title {
            margin: 18px 0 6px;
            color: #0b1220;
            font-size: 22px;
            font-weight: 950;
            text-align: center;
            letter-spacing: -0.03em;
          }

          .pv4-launch-text {
            color: #475569;
            font-size: 13px;
            font-weight: 800;
            text-align: center;
          }

          .pv4-launch-loader {
            width: 160px;
            height: 7px;
            border-radius: 999px;
            background: #e2e8f0;
            overflow: hidden;
            margin-top: 18px;
          }

          .pv4-launch-loader span {
            display: block;
            width: 100%;
            height: 100%;
            border-radius: inherit;
            background: var(--pv4-accent, #075985);
            transform-origin: left;
            animation: pv4-loader-fill 0.78s ease forwards;
          }

          @keyframes pv4-launch-pop {
            0% {
              opacity: 0;
              transform: translateY(30px) scale(0.65);
            }
            45% {
              opacity: 1;
              transform: translateY(-6px) scale(1.06);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          @keyframes pv4-loader-fill {
            0% { transform: scaleX(0); }
            100% { transform: scaleX(1); }
          }

          .pv4-empty-state {
            background: #ffffff;
            border: 1px dashed #94a3b8;
            border-radius: 20px;
            padding: 28px 18px;
            text-align: center;
            color: #334155;
            font-weight: 800;
          }

          .pv4-empty-state h4 {
            margin: 0 0 8px;
            color: #0b1220;
            font-weight: 950;
          }

          .pv4-helper {
            margin-top: 14px;
            background: rgba(255,255,255,0.96);
            border: 1px solid #cbd5e1;
            border-radius: 16px;
            padding: 10px 13px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            color: #075985;
            font-size: 12px;
            font-weight: 850;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.04);
          }

          .pv4-skeleton-card {
            border-radius: 21px;
            background: #ffffff;
            border: 1px solid #cbd5e1;
            min-height: 146px;
            padding: 15px;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }

          .pv4-skeleton-logo,
          .pv4-skeleton-line {
            background: linear-gradient(90deg, #e2e8f0 25%, #cbd5e1 37%, #e2e8f0 63%);
            background-size: 400% 100%;
            animation: pv4-shimmer 1.4s ease infinite;
          }

          .pv4-skeleton-logo {
            width: 62px;
            height: 62px;
            border-radius: 20px;
          }

          .pv4-skeleton-line {
            width: 78%;
            height: 14px;
            border-radius: 999px;
            margin-top: 14px;
          }

          @keyframes pv4-shimmer {
            0% { background-position: 100% 0; }
            100% { background-position: -100% 0; }
          }

          @media (max-width: 980px) {
            .pv4-icon-grid {
              grid-template-columns: repeat(3, minmax(0, 1fr));
            }
          }

          @media (max-width: 768px) {
            .pv4-home-page {
              padding-top: 4px;
            }

            .pv4-hero {
              border-radius: 19px;
              padding: 15px;
            }

            .pv4-title {
              font-size: 23px;
            }

            .pv4-profile-card {
              width: 100%;
              min-width: 0;
            }

            .pv4-toolbar-row {
              grid-template-columns: 1fr;
            }

            .pv4-actions {
              width: 100%;
            }

            .pv4-btn {
              flex: 1;
            }

            .pv4-board-title-row {
              align-items: flex-start;
            }

            .pv4-pills {
              width: 100%;
            }

            .pv4-pill {
              flex: 1;
              text-align: center;
            }

            .pv4-icon-grid {
              grid-template-columns: repeat(3, minmax(0, 1fr));
              gap: 10px;
            }

            .pv4-app-card {
              min-height: 132px;
              border-radius: 19px;
              padding: 14px 10px 12px;
            }

            .pv4-icon-tile,
            .pv4-logo-tile {
              width: 54px;
              height: 54px;
              border-radius: 18px;
            }

            .pv4-icon-tile svg {
              width: 32px;
              height: 32px;
            }

            .pv4-app-logo {
              width: 35px;
              height: 35px;
            }

            .pv4-app-name {
              font-size: 15px;
            }

            .pv4-hover-scene {
              display: none;
            }
          }

          @media (max-width: 520px) {
            .pv4-icon-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .pv4-app-card {
              min-height: 128px;
            }

            .pv4-helper {
              flex-direction: column;
              text-align: center;
            }
          }

          @media (max-width: 350px) {
            .pv4-icon-grid {
              grid-template-columns: 1fr;
            }
          }
        `}
      </style>

      {launchingApp && (
        <div className="pv4-launch-overlay">
          <div
            className="pv4-launch-card"
            style={{
              "--pv4-accent": launchingApp.preset.accent,
              "--pv4-soft": launchingApp.preset.soft,
              "--pv4-border": launchingApp.preset.border,
            }}
          >
            <CategoryIcon preset={launchingApp.preset} large />
            <h2 className="pv4-launch-title">{launchingApp.appName}</h2>
            <div className="pv4-launch-text">Opening workspace...</div>
            <div className="pv4-launch-loader">
              <span />
            </div>
          </div>
        </div>
      )}

      <div className="pv4-shell">
        <div className="pv4-hero">
          <div className="pv4-hero-inner">
            <div>
              <div className="pv4-kicker">
                <span className="pv4-kicker-dot" />
                <span>Paisa Vara Workspace</span>
              </div>

              <h1 className="pv4-title">Launch Your Apps</h1>
            </div>

            <div className="pv4-profile-card">
              <div className="pv4-avatar">{getUserInitials()}</div>
              <div>
                <div className="pv4-profile-name">{getUserDisplayName()}</div>
                <div className="pv4-profile-role">
                  {userRole || "Store Owner"} {user?.store_name ? `• ${user.store_name}` : ""}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="pv4-toolbar">
          <div className="pv4-toolbar-row">
            <div className="pv4-search-wrap">
              <span className="pv4-search-icon">⌕</span>
              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search applications..."
                className="pv4-search-input"
              />
            </div>

            <div className="pv4-actions">
              <button
                type="button"
                onClick={handleDefaultPathClick}
                className="pv4-btn pv4-btn-primary"
              >
                {showDefaultRadio ? "Cancel" : "Set Default"}
              </button>

              {selectedDefaultApp && (
                <button
                  type="button"
                  onClick={handleClearDefault}
                  className="pv4-btn pv4-btn-danger"
                >
                  Clear Default
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="pv4-board-title-row">
          <h2 className="pv4-board-title">
            {showDefaultRadio ? "Choose Default App" : "Available Applications"}
          </h2>

          <div className="pv4-pills">
            <span className="pv4-pill pv4-pill-green">
              {allAvailableApps.length} Apps
            </span>
            <span className="pv4-pill pv4-pill-blue">
              {userRole || "Store Owner"}
            </span>
            <span className="pv4-pill pv4-pill-gray">
              {selectedDefaultApp ? "Default Set" : "No Default"}
            </span>
          </div>
        </div>

        {selectedDefaultApp && !showDefaultRadio && (
          <div className="pv4-default-strip">
            <span>
              Default app: <strong>{selectedDefaultAppName}</strong>
            </span>
            <span>Use “Clear Default” to stop auto-opening this app.</span>
          </div>
        )}

        {isLoading ? (
          renderLoadingCards()
        ) : filteredApps.length > 0 ? (
          <div className="pv4-icon-grid">
            {filteredApps.map((app, appIndex) => {
              const isDefault = selectedDefaultApp === app.key;
              const preset = getVisualPreset(app);

              return <React.Fragment key={app.key}>
                {startsCatalogSection(filteredApps, appIndex) && (
                  <div className="pv4-section-title">{app.catalogSection}</div>
                )}
                <div
                  className={`pv4-app-card ${isDefault ? "is-default" : ""} ${
                    showDefaultRadio ? "default-mode" : ""
                  }`}
                  style={{
                    "--pv4-accent": preset.accent,
                    "--pv4-soft": preset.soft,
                    "--pv4-border": preset.border,
                  }}
                  onClick={() => {
                    if (showDefaultRadio) {
                      handleDefaultAppSelection(app.key);
                    } else {
                      handleAppClick(app);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();

                      if (showDefaultRadio) {
                        handleDefaultAppSelection(app.key);
                      } else {
                        handleAppClick(app);
                      }
                    }
                  }}
                >
                  <span className="pv4-card-shadow" />

                  {showDefaultRadio ? (
                    <div className="pv4-radio-wrap">
                      <input
                        type="radio"
                        name="defaultApp"
                        checked={isDefault}
                        onChange={() => handleDefaultAppSelection(app.key)}
                        onClick={(event) => event.stopPropagation()}
                      />
                    </div>
                  ) : (
                    isDefault && <div className="pv4-default-tag">DEFAULT</div>
                  )}

                  <div className="pv4-hover-scene" aria-hidden="true">
                    {[1, 2, 3, 4, 5, 6].map((index) => (
                      <MiniParticle key={`${app.key}-particle-${index}`} preset={preset} index={index} />
                    ))}
                  </div>

                  <div
                    className="pv4-logo-tile"
                    style={{
                      "--pv4-accent": preset.accent,
                      "--pv4-soft": preset.soft,
                      "--pv4-border": preset.border,
                    }}
                  >
                    <img
                      src={app.logo}
                      alt={app.name || app.displayName}
                      className="pv4-app-logo"
                    />
                  </div>
                  <h3 className="pv4-app-name">{app.displayName}</h3>

                  <div className="pv4-launch-hint">
                    {showDefaultRadio ? "Set default" : "Launch"}
                  </div>
                </div>
              </React.Fragment>;
            })}
          </div>
        ) : (
          <div className="pv4-empty-state">
            <h4>No applications found</h4>
            <p>
              {searchQuery
                ? "No app matches your search. Try another keyword."
                : "No additional applications are available. Please check your subscription or permissions."}
            </p>
          </div>
        )}

        <div className="pv4-helper">
          <span>Tip:</span>
          <span>You can also access applications from the header menu.</span>
        </div>
      </div>
    </div>
  );
}

export default Home;
